import AddIcon from '@mui/icons-material/Add';
import CampaignIcon from '@mui/icons-material/Campaign';
import FilterListIcon from '@mui/icons-material/FilterList';
import ForumIcon from '@mui/icons-material/Forum';
import PaidIcon from '@mui/icons-material/Paid';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import {
    Alert,
    Box,
    Button,
    Collapse,
    Divider,
    Grid,
    LinearProgress,
    MenuItem,
    Pagination,
    PaletteColor,
    Select,
    Skeleton,
    Stack,
    Switch,
    Theme,
    Typography,
    useTheme,
} from '@mui/material';
import dinero from 'dinero.js';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import BackNavigationButton from '../../../components/common/BackNavigationButton';
import LargeMaterialUISwitch from '../../../components/common/LargeMaterialUISwitch';
import PageContainer from '../../../components/common/PageContainter';
import StyledCard from '../../../components/common/StyledCard';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import SextforceAutoCampaignTipReplyAllTippersListDialog from '../../../components/services/hostess/autoCampaignTipReply/SextforceAutoCampaignTipReplyAllTippersListDialog';
import SextforceAutoCampaignTipReplyNewCampaignForm from '../../../components/services/hostess/autoCampaignTipReply/SextforceAutoCampaignTipReplyNewCampaignForm';
import SextforceAutoCampaignTipReplySearchBar from '../../../components/services/hostess/autoCampaignTipReply/SextforceAutoCampaignTipReplySearchBar';
import SextforceAutoCampaignTipReplyTableRow from '../../../components/services/hostess/autoCampaignTipReply/SextforceAutoCampaignTipReplyTableRow';
import useSextforceAutoCampaignTipReply, { SextforceAutoCampaignTipReplySortBy } from '../../../hooks/useSextforceAutoCampaignTipReply';
import useSubscriber from '../../../hooks/useSubscriber';
import useSubscriberSettingsNotifications, {
    OnlyFansSettingsNotificationsTransport,
} from '../../../hooks/useSubscriberSettingsNotifications';
import { SettingsContext } from '../../../store/SettingsContext';

const SextforceAutoCampaignTipReplyDescription = () => {
    const [showMore, setShowMore] = useState<boolean>(false);

    return (
        <>
            <Typography variant="body1">
                Auto Campaign Tip Reply allows you to automatically send a reply to tips/contributions to a campaign on your page. You can
                choose to send a simple thank you message or even content to the tipper. You can also add the tipper to a list of your
                choosing.
            </Typography>
            <Collapse in={showMore}>
                <Typography variant="body1" gutterBottom sx={{ mt: 1 }}>
                    Simply enable the service below, add the campaign you want the system to monitor, and create a reply for each tip option
                    for that campaign. The system will automatically send a reply to the tipper when a tip is received.
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Note: If you change the tips options/prices of the campaign on OnlyFans, click on the Edit button of the campaign below
                    to refresh the prices and hit save.
                </Typography>
            </Collapse>
            <Button
                size="small"
                sx={{ mt: 0.5, p: 0, minWidth: 0 }}
                onClick={() => {
                    setShowMore(currentShowMore => !currentShowMore);
                }}
            >
                {showMore ? 'Less' : 'More'}
            </Button>
        </>
    );
};

const CounterCard = ({
    title,
    count,
    color,
    isCurrency,
    icon,
}: {
    title: string;
    count: number;
    color: PaletteColor;
    isCurrency?: boolean;
    icon?: React.ReactNode;
}) => {
    return (
        <Box
            sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                paddingX: 2,
                paddingY: 1.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1.5,
            }}
        >
            {icon && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid',
                        borderColor: color.main,
                        color: color.main,
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </Box>
            )}
            <Stack direction="column" spacing={0} sx={{ minWidth: 0 }}>
                <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.8, whiteSpace: 'nowrap' }}>
                    {title}
                </Typography>
                <Typography variant="h6" fontWeight={700} noWrap>
                    {isCurrency ? dinero({ amount: Math.trunc(count * 100), currency: 'USD' }).toFormat() : count.toLocaleString()}
                </Typography>
            </Stack>
        </Box>
    );
};

const SextforceAutoCampaignTipReply = () => {
    const settingsContext = useContext(SettingsContext);
    const theme: Theme = useTheme();
    const timezone: string = moment.tz.guess();
    const [searchParams] = useSearchParams();

    const {
        data: subscriber,
        isLoading: isSubscriberLoading,
        setServiceRunning,
        setServiceRunningLoading,
        setServiceSettings,
    } = useSubscriber();

    const [showAddNewForm, setShowAddNewForm] = useState<boolean>(
        searchParams.has('campaignUrl') && searchParams.get('campaignUrl')!.length > 0 ? true : false,
    );
    const [showAllTippersDialog, setShowAllTippersDialog] = useState<boolean>(false);
    const [serviceActive, setServiceActive] = useState<boolean>(false);
    const delayBeforeSendingMessage: boolean = subscriber?.sextforce?.autoCampaignReply?.delayBeforeSendingMessage || false;

    // OnlyFans Notifications Settings
    const { data: onlyFansNotificationSettings, isLoading: onlyFansNotificationSettingsLoading } = useSubscriberSettingsNotifications();
    const youAndYourPosts: OnlyFansSettingsNotificationsTransport.Response | undefined = onlyFansNotificationSettings?.find(
        n => n.code === 'you_and_your_posts',
    );
    const newSubscriberNotifications: OnlyFansSettingsNotificationsTransport.Item | undefined = youAndYourPosts?.items.find(
        n => n.code === 'new_tips_fund_raising',
    );
    const siteNotificationsOn: boolean = newSubscriberNotifications?.isEnabled ?? false;

    // Search
    const [query, setQuery] = useState<string>('');
    const [startDate, setStartDate] = useState<Date>(moment().subtract(1, 'year').toDate());
    const [endDate, setEndDate] = useState<Date>(moment().endOf('day').toDate());
    const [active, setActive] = useState<boolean>(true);
    const [sortBy, setSortBy] = useState<SextforceAutoCampaignTipReplySortBy>('postDate');
    const {
        data: autoCampaignTipReplies,
        refetch: refetchAutoCampaignTipRepllies,
        isLoading: isAutoCampaignTipRepliesLoading,
        updateAutoCampaignTipReply,
        deleteAutoCampaignTipReply,
        createAutoCampaignTipReplyTipMessageDetails,
        updateAutoCampaignTipReplyTipMessageDetails,
        deleteAutoCampaignTipReplyTipMessageDetails,
    } = useSextforceAutoCampaignTipReply(query, startDate, endDate, timezone, active, sortBy);

    // Filters visibility and client-side pagination of the campaigns list
    const [showFilters, setShowFilters] = useState<boolean>(true);
    const [page, setPage] = useState<number>(1);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);

    const campaignsList = autoCampaignTipReplies?.list || [];
    const pageCount = Math.max(1, Math.ceil(campaignsList.length / rowsPerPage));
    const paginatedCampaigns = campaignsList.slice((page - 1) * rowsPerPage, page * rowsPerPage);
    const paginationFrom = campaignsList.length === 0 ? 0 : (page - 1) * rowsPerPage + 1;
    const paginationTo = Math.min(page * rowsPerPage, campaignsList.length);

    // Go back to the first page when the filters or the list change
    useEffect(() => {
        setPage(1);
    }, [query, active, sortBy, rowsPerPage, campaignsList.length]);

    useEffect(() => {
        if (subscriber) {
            setServiceActive(subscriber.sextforce?.autoCampaignReply?.active || false);
        } else {
            setServiceActive(false);
        }
    }, [subscriber]);

    return (
        <PageContainer>
            {subscriber && '_id' in subscriber && subscriber._id && (
                <>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        <BackNavigationButton url={`/subscribers/${subscriber._id}/${settingsContext.services.messageUsers.homeUrl}`} />{' '}
                        Auto-Reply to Campaign Tip for {subscriber.username}
                    </Typography>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Auto-Reply to Campaign Tips" />
                    </StyledCard>
                    <StyledCard noCard>
                        <Alert severity="info">
                            <SextforceAutoCampaignTipReplyDescription />
                        </Alert>
                    </StyledCard>

                    <StyledCard>
                        <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                            <Grid item xs="auto">
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid',
                                        borderColor: 'primary.main',
                                        color: 'primary.main',
                                        borderRadius: '50%',
                                        width: 40,
                                        height: 40,
                                        flexShrink: 0,
                                    }}
                                >
                                    <VerifiedUserOutlinedIcon fontSize="small" />
                                </Box>
                            </Grid>
                            <Grid item xs>
                                Auto-Reply to Campaign Tip is currently{' '}
                                {serviceActive ? (
                                    <span style={{ color: theme.palette.success.dark }}>running</span>
                                ) : (
                                    <span style={{ color: theme.palette.error.main }}>not running</span>
                                )}
                                .
                            </Grid>
                            <Grid item xs="auto">
                                {isSubscriberLoading || setServiceRunningLoading ? (
                                    <Skeleton width={'100%'} height={51} />
                                ) : (
                                    <LargeMaterialUISwitch
                                        checked={serviceActive}
                                        onChange={e => {
                                            setServiceRunning('sextforce.autoCampaignReply', e.target.checked);
                                            setActive(e.target.checked);
                                        }}
                                        color="primary"
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Divider />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction={'row'} spacing={1} alignItems="center" justifyContent={'space-between'}>
                                    <Stack direction={'column'} spacing={1} alignItems="flex-start">
                                        <Typography variant="body1">Delay before sending message?</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            If enabled, the system will wait a short random time before sending the reply to the tipper.
                                        </Typography>
                                    </Stack>
                                    <Switch
                                        checked={delayBeforeSendingMessage}
                                        size="medium"
                                        onChange={e => {
                                            setServiceSettings('sextforce.autoCampaignReply', {
                                                active: serviceActive,
                                                delayBeforeSendingMessage: e.target.checked,
                                            });
                                        }}
                                    />
                                </Stack>
                            </Grid>
                        </Grid>
                    </StyledCard>

                    {!onlyFansNotificationSettingsLoading && !siteNotificationsOn && (
                        <StyledCard noCard>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body1">
                                    <strong>Important:</strong> For this service to work, you must have the "New Campaign Contribution"
                                    notification turned on in{' '}
                                    <Link to={'https://onlyfans.com/my/settings/notifications/site'} target="_blank">
                                        OnlyFans Notifications Settings
                                    </Link>
                                    .
                                </Typography>
                            </Alert>
                        </StyledCard>
                    )}

                    <StyledCard noCard>
                        <Grid container spacing={1} alignItems="stretch">
                            <Grid item xs={12} sm={4}>
                                <CounterCard
                                    title="Campaigns"
                                    count={autoCampaignTipReplies?.metadata.totalCampaigns || 0}
                                    color={theme.palette.primary}
                                    icon={<CampaignIcon fontSize="small" />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <CounterCard
                                    title="Replies Sent"
                                    count={autoCampaignTipReplies?.metadata.totalReplies || 0}
                                    color={theme.palette.primary}
                                    icon={<ForumIcon fontSize="small" />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <CounterCard
                                    title="Total Tips"
                                    count={autoCampaignTipReplies?.metadata.totalTipsAmount || 0}
                                    color={theme.palette.primary}
                                    isCurrency
                                    icon={<PaidIcon fontSize="small" />}
                                />
                            </Grid>
                        </Grid>
                    </StyledCard>

                    {!showAddNewForm && (
                        <StyledCard noCard>
                            <Button
                                variant="contained"
                                color="secondary"
                                fullWidth
                                size="large"
                                onClick={() => {
                                    setShowAddNewForm(true);
                                }}
                                startIcon={<AddIcon />}
                            >
                                Add New Tip Campaign
                            </Button>
                        </StyledCard>
                    )}

                    {showAddNewForm && (
                        <SextforceAutoCampaignTipReplyNewCampaignForm
                            subscriber={subscriber}
                            refetchAutoCampaignTipRepllies={refetchAutoCampaignTipRepllies}
                            showAddNewForm={showAddNewForm}
                            setShowAddNewForm={setShowAddNewForm}
                            handleCancel={() => {
                                setShowAddNewForm(false);
                            }}
                        />
                    )}

                    <StyledCard noCard>
                        <Button
                            variant="contained"
                            color="info"
                            fullWidth
                            size="large"
                            onClick={() => {
                                setShowAllTippersDialog(true);
                            }}
                            startIcon={<PeopleAltIcon />}
                        >
                            Show All Tippers
                        </Button>
                    </StyledCard>

                    {showAllTippersDialog && (
                        <SextforceAutoCampaignTipReplyAllTippersListDialog
                            open={showAllTippersDialog}
                            onClose={() => {
                                setShowAllTippersDialog(false);
                            }}
                            theme={theme}
                        />
                    )}

                    <StyledCard sx={{ p: { xs: 1, sm: 2 } }}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="h6">
                                Tip Campaigns ({autoCampaignTipReplies ? autoCampaignTipReplies.list.length.toLocaleString() : 0})
                            </Typography>
                            <Button
                                size="small"
                                color="primary"
                                startIcon={<FilterListIcon />}
                                onClick={() => {
                                    setShowFilters(currentShowFilters => !currentShowFilters);
                                }}
                            >
                                Filters
                            </Button>
                        </Stack>

                        {isAutoCampaignTipRepliesLoading && <LinearProgress sx={{ mb: 1 }} />}

                        {showFilters && (
                            <SextforceAutoCampaignTipReplySearchBar
                                isLoading={isAutoCampaignTipRepliesLoading}
                                active={active}
                                setActive={setActive}
                                setQuery={setQuery}
                                startDate={startDate}
                                setStartDate={setStartDate}
                                endDate={endDate}
                                setEndDate={setEndDate}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                theme={theme}
                            />
                        )}

                        {!isAutoCampaignTipRepliesLoading && campaignsList.length === 0 && (
                            <Typography
                                variant="body1"
                                sx={{
                                    textAlign: 'center',
                                    color: theme.palette.text.secondary,
                                    py: 2,
                                }}
                            >
                                No tip campaigns yet
                            </Typography>
                        )}

                        {campaignsList.length > 0 && (
                            <>
                                <Stack direction="column" spacing={2}>
                                    {paginatedCampaigns.map(autoCampaignTipReply => (
                                        <SextforceAutoCampaignTipReplyTableRow
                                            key={autoCampaignTipReply._id}
                                            autoCampaignTipReply={autoCampaignTipReply}
                                            updateAutoCampaignTipReply={updateAutoCampaignTipReply}
                                            deleteAutoCampaignTipReply={deleteAutoCampaignTipReply}
                                            refetchAutoCampaignTipReplies={refetchAutoCampaignTipRepllies}
                                            createAutoCampaignTipReplyTipMessageDetails={createAutoCampaignTipReplyTipMessageDetails}
                                            updateAutoCampaignTipReplyTipMessageDetails={updateAutoCampaignTipReplyTipMessageDetails}
                                            deleteAutoCampaignTipReplyTipMessageDetails={deleteAutoCampaignTipReplyTipMessageDetails}
                                            subscriber={subscriber}
                                            theme={theme}
                                        />
                                    ))}
                                </Stack>

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    justifyContent="space-between"
                                    flexWrap="wrap"
                                    useFlexGap
                                    sx={{ mt: 2 }}
                                >
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {paginationFrom.toLocaleString()}–{paginationTo.toLocaleString()} of{' '}
                                        {campaignsList.length.toLocaleString()}
                                    </Typography>
                                    <Pagination
                                        size="small"
                                        count={pageCount}
                                        page={page}
                                        onChange={(e, value) => {
                                            setPage(value);
                                        }}
                                        siblingCount={0}
                                        boundaryCount={1}
                                    />
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            Rows:
                                        </Typography>
                                        <Select
                                            size="small"
                                            value={rowsPerPage}
                                            onChange={e => {
                                                setRowsPerPage(Number(e.target.value));
                                            }}
                                        >
                                            <MenuItem value={10}>10</MenuItem>
                                            <MenuItem value={25}>25</MenuItem>
                                            <MenuItem value={50}>50</MenuItem>
                                        </Select>
                                    </Stack>
                                </Stack>
                            </>
                        )}
                    </StyledCard>
                </>
            )}
        </PageContainer>
    );
};

export default SextforceAutoCampaignTipReply;
